import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Users, UserCheck, Headphones, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import './EnglishPage.css';

const EnglishPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const lmsScreenshots = [
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/o8zva88j_01a.png",
      title: "My Learning Adventure",
      description: "Your personalized dashboard showing progress across all 10 levels, completed lessons, and upcoming activities."
    },
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/m3x8qmhl_02a.png",
      title: "Lesson Overview - Skies English",
      description: "Navigate through comprehensive lesson plans with clear learning objectives and structured units."
    },
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/m4qakga7_03a.png",
      title: "Interactive Flipcards - Learn New Words",
      description: "One side shows an image, the other reveals the word with audio pronunciation. Flip to learn vocabulary naturally."
    },
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/9jmxpyvn_04a.png",
      title: "Listen-Record-Compare",
      description: "Practice pronunciation using the Callan method - listen, record yourself, and compare with native speakers."
    },
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/p6fsupsn_07a.png",
      title: "Audio Stories & Quizzes",
      description: "Listen to real-world stories with synchronized text, then test your understanding with engaging quizzes that provide instant feedback."
    },
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/5ww4urnb_08a.png",
      title: "Progress Tracking",
      description: "Visual analytics showing your learning journey, strengths, and areas for improvement across all activities."
    },
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/fwfal21w_09a.png",
      title: "Placement Test",
      description: "Comprehensive assessment to determine your starting level and create a personalized learning path tailored to your needs."
    },
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/whm9uk2a_10a.png",
      title: "Peer Collaboration Sessions",
      description: "Book one-to-one sessions with fellow learners to practice conversation, discuss stories, and build confidence together."
    },
    {
      url: "https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/91sed1lb_11a.png",
      title: "Expert Mentorship & Feedback",
      description: "Receive personalized guidance from local professors and expat volunteers with detailed feedback on your progress."
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % lmsScreenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + lmsScreenshots.length) % lmsScreenshots.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="english-page">
      {/* Hero Section */}
      <section className="page-hero english-hero" data-testid="english-hero">
        <div className="hero-with-ladders">
          <img 
            src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/kc5vzhwq_laddergirltr.png"
            alt="Learning journey"
            className="hero-ladder hero-ladder-left"
          />
          
          <div className="page-hero-content">
            <div className="hero-icon-wrapper">
              <BookOpen size={80} color="white" className="hero-book-icon" />
            </div>
            <h1>Learn English</h1>
            <p>Empowering Independent and Collaborative Learning for Sustainability</p>
          </div>
          
          <img 
            src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/t4srckf6_ladderboytr.png"
            alt="Learning journey"
            className="hero-ladder hero-ladder-right"
          />
        </div>
      </section>

      {/* Main Content */}
      <section className="section">
        <div className="content-wrapper">
          <h2 className="section-title">Why English Meets Sustainability</h2>
          <p className="intro-text">
            Learning English is essential, but addressing pressing issues like extreme heat, water shortages, 
            and climate migration is just as critical for Indian youth. <strong>Litvill Lessons not only nurtures 
            excellent English speakers but empowers future sustainability leaders to create resilient, inclusive 
            communities.</strong>
          </p>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">What We Offer</h2>
          <div className="stats-grid">
            <div className="stat-box">
              <div className="stat-number">10</div>
              <div className="stat-label">Learning Levels</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">200+</div>
              <div className="stat-label">Comprehensive Lessons</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">2000+</div>
              <div className="stat-label">Learning Units</div>
            </div>
          </div>

          <div className="content-features">
            <div className="feature-item">
              <Headphones size={32} color="var(--primary)" />
              <h4>Audio-Rich Stories</h4>
              <p>Engaging narratives covering real-world topics: poverty, inequality, environmental issues</p>
            </div>
            <div className="feature-item">
              <BarChart3 size={32} color="var(--primary)" />
              <h4>Placement Tests</h4>
              <p>Start at the right level with our comprehensive assessment system</p>
            </div>
            <div className="feature-item">
              <BookOpen size={32} color="var(--primary)" />
              <h4>Diverse Content</h4>
              <p>Stories, flipcards, quizzes, Listen-Record-Compare (Callan method), discussions</p>
            </div>
          </div>
        </div>
      </section>

      {/* LMS Preview - Interactive Carousel */}
      <section className="section lms-preview">
        <div className="content-wrapper">
          <h2 className="section-title">Our Learning Management System</h2>
          <p className="intro-text" style={{ marginBottom: '2rem' }}>
            Explore our colorful, engaging platform designed to make learning English fun and meaningful
          </p>
          
          <div className="carousel-container">
            {/* Main Carousel */}
            <div className="carousel-main">
              <button 
                className="carousel-btn carousel-btn-prev" 
                onClick={prevSlide}
                aria-label="Previous screenshot"
              >
                <ChevronLeft size={32} />
              </button>
              
              <div className="carousel-content">
                <div className="carousel-image-wrapper">
                  <img 
                    src={lmsScreenshots[currentSlide].url} 
                    alt={lmsScreenshots[currentSlide].title}
                    className="carousel-image"
                  />
                </div>
                
                <div className="carousel-caption">
                  <h3>{lmsScreenshots[currentSlide].title}</h3>
                  <p>{lmsScreenshots[currentSlide].description}</p>
                  <div className="carousel-counter">
                    {currentSlide + 1} / {lmsScreenshots.length}
                  </div>
                </div>
              </div>
              
              <button 
                className="carousel-btn carousel-btn-next" 
                onClick={nextSlide}
                aria-label="Next screenshot"
              >
                <ChevronRight size={32} />
              </button>
            </div>
            
            {/* Thumbnail Navigation */}
            <div className="carousel-thumbnails">
              {lmsScreenshots.map((screenshot, index) => (
                <button
                  key={index}
                  className={`carousel-thumbnail ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to ${screenshot.title}`}
                >
                  <img src={screenshot.url} alt={screenshot.title} />
                  <div className="thumbnail-overlay">
                    <span>{index + 1}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
{/* How We Teach English — the Smart, Modern Way */}
<section className="section" style={{ backgroundColor: 'var(--light-grey)', padding: '4rem 0' }}>
  <div className="content-wrapper" style={{ maxWidth: '1100px' }}>
    
    <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
      How We Teach English — the Smart, Modern Way
    </h2>

    <p className="intro-text" style={{
      maxWidth: '850px',
      margin: '0 auto 3rem auto',
      textAlign: 'center',
      fontSize: '1.2rem',
      lineHeight: '1.7'
    }}>
      Kids don’t learn by memorizing rules. They learn by using language — in stories,
      in conversations, and in small, fun activities. Our program follows how the brain naturally learns:
    </p>

    {/* Features grid */}
    <div className="content-features how-we-teach-grid">
      {[
        {
          icon: BookOpen,
          highlight: "New words",
          text: "come through pictures, sound, and movement"
        },
        {
          icon: Headphones,
          highlight: "Short activities",
          text: "help kids remember and use new language"
        },
        {
          icon: Users,
          highlight: "Stories and situations",
          text: "make the language meaningful and exciting"
        },
        {
          icon: UserCheck,
          highlight: "Grammar through patterns",
          text: "is introduced naturally, not through heavy rules"
        },
        {
          icon: BarChart3,
          highlight: "Smart practice",
          text: "helps kids avoid mistakes and gain confidence"
        },
        {
          icon: BookOpen,
          highlight: "Listening + reading",
          text: "build real understanding of the language"
        },
        {
          icon: Headphones,
          highlight: "Speaking practice",
          text: "brings everything together in real conversations"
        }
      ].map(({ icon: Icon, highlight, text }, i) => (
        <div key={i} className="feature-item how-we-teach-card">
          <div className="how-we-teach-card-icon">
            <Icon size={28} />
          </div>
          <p>
            <span className="how-we-teach-highlight">{highlight}</span>{" "}
            {text}
          </p>
        </div>
      ))}
    </div>


    {/* Result Box */}
    <div style={{
      background: 'var(--primary)',
      color: 'white',
      padding: '3rem',
      borderRadius: '18px',
      textAlign: 'center',
      marginTop: '4rem',
      boxShadow: '0px 8px 20px rgba(0,0,0,0.12)'
    }}>
      <h3 style={{ fontSize: '2rem', marginBottom: '1rem' }}>The result?</h3>
      <p style={{
        fontSize: '1.4rem',
        maxWidth: '750px',
        margin: '0 auto',
        lineHeight: '1.6'
      }}>
        Kids don’t just “study” English. <strong>They become English speakers</strong> — confident,
        curious, and ready for the world.
      </p>
    </div>
  </div>
</section>


      {/* The Litvill Formula */}
      <section className="section formula-section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">The Litvill Formula: 60-30-10</h2>
          
          <div className="formula-grid">
            {/* 60% Self-directed */}
            <div className="formula-card">
              <div className="formula-percentage" style={{ color: 'var(--primary)' }}>60%</div>
              <h3>Self-Directed Learning</h3>
              <p className="formula-intro">
                In a rapidly changing world, self-reliance and adaptability are essential. Our bite-sized 
                units are designed for independent learning, helping participants develop critical thinking, 
                decision-making skills, and the confidence to solve real-world challenges.
              </p>
              <ul className="formula-list">
                <li>10 progressive levels with placement tests</li>
                <li>200+ comprehensive lessons</li>
                <li>2000+ interactive units (stories, quizzes, discussions)</li>
                <li>Learn at your own pace, anytime</li>
              </ul>
              <div className="formula-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/t8baj48o_lesson.png"
                  alt="Self-directed learning interface"
                  className="formula-screenshot"
                />
              </div>
            </div>

            {/* 30% Peer Collaboration */}
            <div className="formula-card">
              <div className="formula-percentage" style={{ color: 'var(--accent)' }}>30%</div>
              <h3>Peer Collaboration</h3>
              <p className="formula-intro">
                The best way to deepen understanding is to teach. Through one-to-one sessions, participants 
                practice with fellow learners, exchanging knowledge and insights. This peer-driven model 
                fosters communication and leadership skills while offering opportunities for personal growth.
              </p>
              <ul className="formula-list">
                <li>One-to-one personalized peer sessions</li>
                <li>Practice conversation and comprehension</li>
                <li>Build lasting learning relationships</li>
                <li>Learn by teaching others</li>
              </ul>
              <div className="formula-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/1t5tbk34_mentoring.png"
                  alt="Peer collaboration mentoring session"
                  className="formula-screenshot"
                />
              </div>
            </div>

            {/* 10% Expert Mentorship */}
            <div className="formula-card">
              <div className="formula-percentage" style={{ color: 'var(--highlight)' }}>10%</div>
              <h3>Expert Mentorship</h3>
              <p className="formula-intro">
                Our mentors are not just teachers—they are guides. Local university professors and experienced 
                expats provide personalized feedback and help learners refine their skills. By focusing on 
                growth rather than grades, mentors create a supportive environment for meaningful learning.
              </p>
              <ul className="formula-list">
                <li>Weekly small group sessions (2-6 people)</li>
                <li>Local university professors and expat volunteers</li>
                <li>Practice-focused, not exam-focused</li>
                <li>Personalized feedback and progress tracking</li>
              </ul>
              <div className="formula-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/kq5kjvqi_webinar.png"
                  alt="Expert mentorship webinar session"
                  className="formula-screenshot"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-content">
          <h2>Ready to Start Your English Learning Journey?</h2>
          <p>
            We provide personalized access to our Learning Management System to maintain a quality learning 
            community. Contact us to request access and begin your transformation.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary" data-testid="english-request-access">
              Request Access
            </Link>
            <Link to="/programs" className="btn btn-secondary" data-testid="english-explore-programs">
              Explore Sustainability Programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnglishPage;